import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Bar } from '@src/components/Bar/Bar';
import { Button } from '@src/components/Button/Button';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { classes } from '@src/components/layout/space';
import { Stack } from '@src/components/Stack/Stack';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Drawer.css';

export type DrawerSide = 'start' | 'end';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Action bar outside the scrollable body. */
  footer?: ReactNode;
  /** start — slides in from the left. end — from the right. */
  side?: DrawerSide;
  title?: string;
  titleActions?: ReactNode;
  closeLabel?: string;
  testId?: string;
  className?: string;
}

/**
 * A panel that slides in from one side, with a scrim behind it. Used by the
 * hamburger on a narrow header — not a dropdown.
 */
export function Drawer({
  open,
  onClose,
  children,
  footer,
  side = 'start',
  title = 'Menu',
  titleActions,
  closeLabel = 'Close menu',
  testId,
  className,
}: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    panelRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className={classes('drawer', className)} {...testIdAttr(testId)}>
      <button
        type="button"
        className="drawer__scrim"
        aria-label={closeLabel}
        onClick={onClose}
        {...testIdAttr(testIdFor(testId, 'scrim'))}
      />
      <div
        ref={panelRef}
        className={classes('drawer__panel', `drawer__panel--${side}`)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...testIdAttr(testIdFor(testId, 'panel'))}
      >
        <Bar wrap={false} align="center" gap={4}>
          <div className="drawer__title-row">
            <Typography as="h2" role="title/card" id={titleId}>
              {title}
            </Typography>
            {titleActions}
          </div>
          <Button
            variant="ghost"
            size="small"
            icon={<CloseIcon />}
            aria-label={closeLabel}
            onClick={onClose}
            testId={testIdFor(testId, 'close')}
          />
        </Bar>
        <Stack gap={4} className="drawer__body">
          {children}
        </Stack>
        {footer && <div className="drawer__footer">{footer}</div>}
      </div>
    </div>
  );
}
